import { ReactNode, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import {
  useCoachClient,
  useUnreadMessages,
  useFamilyBilling,
  useCoachingState,
  usePlanHistory,
  useGoals,
  useClientNotes,
  CoachOverviewClient,
} from "../lib/queries";
import { PlanChooser } from "../components/PlanChooser";
import { PlanRequestStatus } from "../components/PlanRequestStatus";
import { OfferedPlansEditor } from "../components/OfferedPlansEditor";
import { SubscriptionState } from "../components/SubscriptionState";
import { LoadingState, ErrorState } from "../components/Loading";
import { PageShell } from "../components/PageShell";
import { ClientGoals, GoalRow, formatTargetDate } from "../components/ClientGoals";
import { SessionHistory } from "../components/SessionHistory";
import { CheckinTrend } from "../components/CheckinTrend";
import { ClientNotes } from "../components/ClientNotes";
import { ProgressChart } from "../components/ProgressChart";
import { OpenFlags } from "../components/OpenFlags";
import { money } from "../components/PricingSettings";
import { WorkspaceTabs, WorkspacePanel, WorkspaceTab, useWorkspaceTab, useIsNarrow } from "../components/Workspace";
import { PlanBuilder } from "./PlanBuilder";
import { parsePlanBody, parseElements, describeSessionLength } from "../lib/planElements";
import { initials } from "../lib/initials";

const BILLING_LABEL: Record<string, string> = {
  pending: "Billing pending",
  active: "Billing active",
  past_due: "Payment past due",
  cancelled: "Subscription cancelled",
};

/** The same states, coloured by what they ask of the coach: fine, waiting, or act now. */
const BILLING_BADGE: Record<string, string> = {
  pending: "badge-warn",
  active: "badge-ok",
  past_due: "badge-danger",
};

const TAB_IDS = ["overview", "plans", "goals", "sessions", "notes", "billing"] as const;
type TabId = (typeof TAB_IDS)[number];

interface PlanSummaryRow {
  id: string;
  title: string;
  status: "draft" | "published" | "archived";
  elements: unknown;
}

interface NoteRow {
  id: string;
  note: string;
  created_at: string;
}

/**
 * Everything the coach has about one athlete, in one place (PRD §5.2).
 *
 * This was one long scroll -- summary, the whole current plan, goals, notes, a chart, every session and
 * the subscription, stacked -- with the plans themselves (drafts, history, the builder) on a separate
 * page. It is now a workspace: who this is and what is waiting on the coach at the top, then one tab per
 * kind of thing. Overview answers "how are they doing" without opening anything else.
 *
 * Reads from the coach overview already in memory rather than fetching per client.
 */
export function ClientDetail() {
  const { clientId } = useParams<{ clientId: string }>();
  const { client, query } = useCoachClient(clientId);

  if (query.isPending) {
    return (
      <PageShell title="Client">
        <LoadingState label="Loading client…" />
      </PageShell>
    );
  }

  if (query.isError) {
    return (
      <PageShell title="Client">
        <ErrorState error={query.error} onRetry={() => query.refetch()} />
      </PageShell>
    );
  }

  // Loaded, but this id isn't in the roster -- a stale link or a deleted client, not a failure.
  if (!client) {
    return (
      <PageShell title="Client">
        <p className="empty-state">That client no longer exists.</p>
        <Link className="link-button" to="/dashboard">
          Back to clients
        </Link>
      </PageShell>
    );
  }

  return <ClientWorkspace client={client} />;
}

/** The old plan-builder address, kept working: plans are the Plans tab of the client's workspace now. */
export function ClientPlansRedirect() {
  const { clientId } = useParams<{ clientId: string }>();
  return <Navigate replace to={`/coach/clients/${clientId}?tab=plans`} />;
}

function ClientWorkspace({ client }: { client: CoachOverviewClient }) {
  const { active, select, isMounted } = useWorkspaceTab(TAB_IDS);
  const narrow = useIsNarrow();
  const history = usePlanHistory<PlanSummaryRow[]>(client.id).data ?? [];
  const summary = useFamilyBilling(client.id);
  const athlete = summary.data?.athletes?.find((entry) => entry.client_id === client.id);

  const drafts = history.filter((plan) => plan.status === "draft");
  const tabs: WorkspaceTab<TabId>[] = [
    { id: "overview", label: "Overview" },
    { id: "plans", label: "Plans", count: drafts.length },
    { id: "goals", label: "Goals" },
    { id: "sessions", label: "Sessions", count: client.open_flags?.length ?? 0 },
    { id: "notes", label: "Notes" },
    { id: "billing", label: "Billing", count: athlete?.plan_request?.status === "pending" ? 1 : 0 },
  ];

  /**
   * The session log stores an index into the plan, not a name, so that renaming an exercise later
   * doesn't rewrite what the athlete did. The plan history turns those indexes back into names.
   */
  const elementNamesByPlan = Object.fromEntries(
    history.map((plan) => [plan.id, parseElements(plan.elements).map((element) => element.name)])
  );

  const canMessage = client.status !== "invited";
  const actions = (
    <>
      {/* Withheld while the invite is outstanding: there is no account on the other end yet, so the
          thread would be a monologue -- the same rule as the dashboard. */}
      {canMessage && (
        <Link className="link-button" to={`/coach/clients/${client.id}/messages`}>
          Messages
          <UnreadDot clientId={client.id} />
        </Link>
      )}
      {/* A link to the Plans tab rather than a button, so it can be opened in a new tab like any page.
          Not offered on the Plans tab itself, where it would only point at the page already open beside
          that tab's own "New plan". */}
      {active !== "plans" && (
        <Link className="link-button workspace-primary" to="?tab=plans">
          {client.current_plan ? "Edit plans" : "Build a plan"}
        </Link>
      )}
    </>
  );

  return (
    <div className="dashboard workspace">
      <WorkspaceHeader client={client} athletePlan={athlete} actions={narrow ? null : actions} />

      {/* Only what is waiting on the coach for this athlete -- a request, a flag, a family's own
          change, a quiet spell. The same panel as the dashboard's, narrowed to one athlete. */}
      <OpenFlags clients={[client]} single />

      <WorkspaceTabs tabs={tabs} active={active} onSelect={select} label={`${client.name}'s sections`} />

      <WorkspacePanel id="overview" active={active} mounted={isMounted("overview")}>
        <ClientOverview client={client} history={history} onOpen={select} />
      </WorkspacePanel>

      {/* The builder, drafts and plan history -- a page of its own until now. */}
      <WorkspacePanel id="plans" active={active} mounted={isMounted("plans")}>
        <PlanBuilder />
      </WorkspacePanel>

      {/* Client-scoped, not plan-scoped (27 Sep debug notes item 8), and the coach's write path onto
          goals. */}
      <WorkspacePanel id="goals" active={active} mounted={isMounted("goals")}>
        <ClientGoals clientId={client.id} canWrite />
      </WorkspacePanel>

      <WorkspacePanel id="sessions" active={active} mounted={isMounted("sessions")}>
        <ProgressChart sessions={client.sessions} />
        <CheckinTrend sessions={client.sessions ?? []} />
        <SessionHistory clientId={client.id} elementNamesByPlan={elementNamesByPlan} />
      </WorkspacePanel>

      <WorkspacePanel id="notes" active={active} mounted={isMounted("notes")}>
        <ClientNotes clientId={client.id} />
      </WorkspacePanel>

      <WorkspacePanel id="billing" active={active} mounted={isMounted("billing")}>
        {client.status !== "inactive" && (
          <>
            <h2>Subscription</h2>
            <AthletePlan client={client} />
          </>
        )}
        <CoachingState client={client} />
      </WorkspacePanel>

      {/* On a phone the two things a coach does most stay under the thumb instead of in the header. */}
      {narrow && <div className="workspace-bottom-bar">{actions}</div>}
    </div>
  );
}

function WorkspaceHeader({
  client,
  athletePlan,
  actions,
}: {
  client: CoachOverviewClient;
  athletePlan: { plan_name: string | null; plan_price_cents: number | null; pending_plan_name: string | null; pending_effective_at: string | null } | undefined;
  actions: ReactNode;
}) {
  const details = [
    client.level,
    client.age !== null ? `age ${client.age}` : null,
    client.is_minor ? "Minor" : "Adult",
    client.guardian_email,
  ]
    .filter(Boolean)
    .join(" · ");
  const lastTrained = latestSession(client);

  return (
    <header className="workspace-header">
      <Link className="workspace-back" to="/dashboard">
        ‹ Clients
      </Link>
      <div className="workspace-identity">
        <div className="workspace-who">
          <span className="client-avatar workspace-avatar">{initials(client.name)}</span>
          <div>
            <h1>{client.name}</h1>
            {details && <p className="workspace-meta">{details}</p>}
          </div>
        </div>
        {actions && <div className="header-actions">{actions}</div>}
      </div>
      <div className="workspace-chips">
        {/* Without this the page looks identical for someone who has signed up and someone who has not,
            and "why can't I message them" has no visible answer. */}
        {client.status === "invited" ? (
          <span className="badge badge-warn">Invite pending</span>
        ) : client.status === "inactive" ? (
          <span className="badge">Not coaching</span>
        ) : (
          <span className="badge badge-ok">Active</span>
        )}
        {athletePlan?.plan_name && (
          <span className="badge">
            {athletePlan.plan_name}
            {athletePlan.plan_price_cents !== null ? ` · ${money(athletePlan.plan_price_cents)}/mo` : ""}
          </span>
        )}
        {/* Read-only for the coach (PRD §5.2), and qualified by what is about to happen to it -- a
            plain "Billing active" beside "Leaving on September 7" contradicted itself (UX review F41). */}
        {client.billing_status && (
          <span className={`badge ${BILLING_BADGE[client.billing_status] ?? ""}`}>
            {BILLING_LABEL[client.billing_status] ?? client.billing_status}
          </span>
        )}
        <SubscriptionState
          client={client}
          sequence={{ pendingPlanName: athletePlan?.pending_plan_name, pendingEffectiveAt: athletePlan?.pending_effective_at }}
        />
        {lastTrained && <span className="badge">Last trained {describeAgo(lastTrained)}</span>}
      </div>
    </header>
  );
}

/**
 * One card per tab: the gist, and the way in. Most visits should be able to end here.
 */
function ClientOverview({
  client,
  history,
  onOpen,
}: {
  client: CoachOverviewClient;
  history: PlanSummaryRow[];
  onOpen: (tab: TabId) => void;
}) {
  const goals = useGoals<GoalRow[]>(client.id).data ?? [];
  const notes = useClientNotes<NoteRow[]>(client.id).data ?? [];

  const plan = client.current_plan;
  const body = plan ? parsePlanBody(plan.elements) : null;
  const drafts = history.filter((entry) => entry.status === "draft");

  const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
  const recent = (client.sessions ?? []).filter((session) => Date.parse(session.completed_at) >= weekAgo);
  const efforts = recent.map((session) => session.session_rpe).filter((rpe): rpe is number => typeof rpe === "number");
  const averageEffort = efforts.length ? (efforts.reduce((sum, rpe) => sum + rpe, 0) / efforts.length).toFixed(1) : null;
  const lastTrained = latestSession(client);

  const achieved = goals.filter((goal) => goal.achieved_at).length;
  const nextGoal = goals
    .filter((goal) => !goal.achieved_at)
    .sort((a, b) => (a.target_date ?? "9999").localeCompare(b.target_date ?? "9999"))[0];
  const latestNote = [...notes].sort((a, b) => b.created_at.localeCompare(a.created_at))[0];

  return (
    <div className="summary-grid">
      <article className="summary-card">
        <p className="summary-label">Current plan</p>
        {plan && body ? (
          <>
            <h3>{plan.title}</h3>
            <p className="form-hint">
              {[
                plan.published_at ? `Published ${formatDate(plan.published_at)}` : null,
                `${body.elements.length} ${body.elements.length === 1 ? "exercise" : "exercises"}`,
                body.groups.length ? `${body.groups.length} ${body.groups.length === 1 ? "circuit" : "circuits"}` : null,
                describeSessionLength(body),
              ]
                .filter(Boolean)
                .join(" · ")}
            </p>
          </>
        ) : (
          <p className="form-hint">No published plan yet.</p>
        )}
        {drafts.length > 0 && (
          <span className="badge badge-draft">
            {drafts.length === 1 ? `Draft waiting: ${drafts[0].title}` : `${drafts.length} drafts waiting`}
          </span>
        )}
        <button type="button" className="chip" onClick={() => onOpen("plans")}>
          {/* "See", like the cards beside it. This used to say "Build a plan" when there was none, which
              put a second copy of the header's main action in the card underneath it. */}
          See plans
        </button>
      </article>

      <article className="summary-card">
        <p className="summary-label">Last 7 days</p>
        <div className="summary-stats">
          <div>
            <span className="summary-stat">{recent.length}</span>
            <span className="form-hint">{recent.length === 1 ? "workout" : "workouts"}</span>
          </div>
          {averageEffort && (
            <div>
              <span className="summary-stat">{averageEffort}</span>
              <span className="form-hint">avg effort</span>
            </div>
          )}
        </div>
        <p className="form-hint">{lastTrained ? `Last trained ${describeAgo(lastTrained)}` : "No sessions logged yet."}</p>
        <button type="button" className="chip" onClick={() => onOpen("sessions")}>
          See sessions
        </button>
      </article>

      <article className="summary-card">
        <p className="summary-label">
          Goals{goals.length ? ` · ${achieved} of ${goals.length} achieved` : ""}
        </p>
        {nextGoal ? (
          <>
            <h3>{nextGoal.text}</h3>
            <p className="form-hint">{nextGoal.target_date ? `Target ${formatTargetDate(nextGoal.target_date)}` : "No target date"}</p>
          </>
        ) : (
          <p className="form-hint">{goals.length ? "Every goal achieved." : "No goals yet."}</p>
        )}
        <button type="button" className="chip" onClick={() => onOpen("goals")}>
          See goals
        </button>
      </article>

      <article className="summary-card">
        <p className="summary-label">Latest note · only you can see these</p>
        {latestNote ? (
          <>
            <p className="summary-note">{latestNote.note}</p>
            <p className="form-hint">{formatDate(latestNote.created_at)}</p>
          </>
        ) : (
          <p className="form-hint">No notes yet.</p>
        )}
        <button type="button" className="chip" onClick={() => onOpen("notes")}>
          See notes
        </button>
      </article>
    </div>
  );
}

/**
 * Stopping and resuming one athlete -- the counterpart to adding one to a family.
 *
 * Stopping waits for the end of the period the family has paid for, and says so before it is confirmed.
 * Nothing is deleted: their plans, sessions and messages stay exactly where they are, because a skater
 * who comes back in September should find their history waiting.
 */
function CoachingState({ client }: { client: CoachOverviewClient }) {
  const coaching = useCoachingState(client.id);
  const [confirming, setConfirming] = useState(false);
  const stopped = client.status === "inactive";

  return (
    <>
      <h2>Coaching</h2>
      {coaching.isError && <ErrorState error={coaching.error} />}

      {stopped ? (
        <>
          <p className="form-hint">
            You&rsquo;re not coaching {client.name} at the moment, and their family isn&rsquo;t being
            billed for them. Everything they did is still here.
          </p>
          <button type="button" disabled={coaching.isPending} onClick={() => coaching.mutate("resume")}>
            {coaching.isPending ? "Starting…" : "Start coaching again"}
          </button>
        </>
      ) : client.pending_inactive_at ? (
        <>
          <p className="form-hint">
            {`${client.name} stops on ${formatDate(client.pending_inactive_at)} — their family is billed for them until then.`}
          </p>
          <button type="button" className="chip" disabled={coaching.isPending} onClick={() => coaching.mutate("resume")}>
            {coaching.isPending ? "Cancelling…" : "Keep coaching them"}
          </button>
        </>
      ) : !confirming ? (
        <button type="button" className="chip" onClick={() => setConfirming(true)}>
          Stop coaching {client.name}
        </button>
      ) : (
        <div className="confirm-box" role="group" aria-label="Confirm stopping">
          <p>
            {client.name} comes off their family&rsquo;s invoice at the end of the month they&rsquo;ve
            already paid for. Their plans, sessions and your notes all stay — you can start again any
            time.
          </p>
          <button type="button" disabled={coaching.isPending} onClick={() => coaching.mutate("stop", { onSuccess: () => setConfirming(false) })}>
            {coaching.isPending ? "Stopping…" : `Yes, stop coaching ${client.name}`}
          </button>
          <button type="button" className="chip" onClick={() => setConfirming(false)}>
            Never mind
          </button>
        </div>
      )}
    </>
  );
}

function latestSession(client: CoachOverviewClient): string | null {
  const dates = (client.sessions ?? []).map((session) => session.completed_at).filter(Boolean);
  return dates.length ? dates.reduce((latest, at) => (at > latest ? at : latest)) : null;
}

/** The day something lands, in the reader's own locale. */
function formatDate(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleDateString(undefined, { month: "long", day: "numeric" });
}

function describeAgo(iso: string): string {
  const days = Math.floor((Date.now() - Date.parse(iso)) / (24 * 60 * 60 * 1000));
  if (Number.isNaN(days)) return iso;
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 7) return `${days} days ago`;
  return `on ${formatDate(iso)}`;
}

/**
 * This athlete's plan, as the coach sees it.
 *
 * Reads the family's own summary so the coach and the family are looking at exactly the same thing --
 * including a queued downgrade, which is the state most likely to cause "why are they still on the
 * expensive one" if only one side can see it.
 */
function AthletePlan({ client }: { client: CoachOverviewClient }) {
  const summary = useFamilyBilling(client.id);
  const athlete = summary.data?.athletes.find((entry) => entry.client_id === client.id);

  if (summary.isPending) return <LoadingState label="Loading plans…" />;
  if (!athlete) return <p className="empty-state">No subscription for this athlete yet.</p>;

  // The coach is changing somebody else's plan and charging somebody else's card, so the chooser is
  // told whose voice to speak in (UX review F42).
  return (
    <>
      <PlanRequestStatus athlete={athlete} actingAs="coach" />
      <PlanChooser athlete={athlete} actingAs="coach" />
      <OfferedPlansEditor athlete={athlete} />
    </>
  );
}

/** A dot on the Messages link when this client's family is waiting on a reply. */
function UnreadDot({ clientId }: { clientId: string }) {
  const unread = useUnreadMessages();
  // Optional-chained through the array too: this drives a dot, and an unexpected response shape must
  // not take down the page it sits on.
  if (!unread.data?.client_ids?.includes(clientId)) return null;
  return <span className="nav-unread-dot" aria-label="Unread messages" />;
}
